import { useEffect, useRef, useCallback } from 'react'
import { useAuth } from './useAuth'
import { supabase } from '../lib/supabase'

// FA-01-session-reliability (FA-01-11): localStorage (not sessionStorage)
// so that two tabs of the same browser share one session token and do
// not self-kick each other.
export const SESSION_TOKEN_KEY = 'erevis_session_token'
export const SESSION_REGISTERED_KEY = 'erevis_session_registered_at'

const CHECK_INTERVAL_MS = 30_000
const STARTUP_DELAY_MS = 8_000    // Wait 8s after mount before first check
const VISIBILITY_DELAY_MS = 5_000 // Wait 5s after tab becomes visible before checking

function generateSessionToken() {
  return crypto.randomUUID()
}

export function getSessionToken() {
  let token = localStorage.getItem(SESSION_TOKEN_KEY)
  if (!token) {
    token = generateSessionToken()
    localStorage.setItem(SESSION_TOKEN_KEY, token)
  }
  return token
}

// Called by useAuth after successful session registration
export function markSessionRegistered() {
  localStorage.setItem(SESSION_REGISTERED_KEY, Date.now().toString())
}

// Shared cleanup used by signOut, cancelBiometric, and the lifted
// failClosed helper in useAuth. Keeps the three exit paths from
// duplicating try/catch.
export function clearSessionLocalStorage() {
  try { localStorage.removeItem(SESSION_TOKEN_KEY) } catch { /* localStorage unavailable */ }
  try { localStorage.removeItem(SESSION_REGISTERED_KEY) } catch { /* localStorage unavailable */ }
}

function isWithinGracePeriod() {
  const registeredAt = localStorage.getItem(SESSION_REGISTERED_KEY)
  if (!registeredAt) return false
  return Date.now() - parseInt(registeredAt, 10) < 15_000 // 15s grace after registration
}

export function useSessionEnforcement() {
  const { session, sessionRef, signOut } = useAuth()
  const intervalRef = useRef(null)
  const kickedRef = useRef(false)
  const startupTimerRef = useRef(null)
  const visibilityTimerRef = useRef(null)

  // Read the access token from sessionRef so checkSession's identity is
  // stable across token refreshes — the useEffect below depends on
  // checkSession and we don't want it tearing down/re-registering every
  // time Supabase rotates a token.
  const checkSession = useCallback(async () => {
    const liveSession = sessionRef?.current || session
    if (!liveSession?.access_token || kickedRef.current) return
    if (typeof document !== 'undefined' && document.hidden) return
    if (isWithinGracePeriod()) return

    try {
      const res = await fetch('/api/auth/session-check', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${liveSession.access_token}`,
        },
        body: JSON.stringify({ sessionToken: getSessionToken() }),
      })

      const data = await res.json()

      if (data.valid === false && data.reason === 'session_replaced') {
        kickedRef.current = true
        clearSessionLocalStorage()
        localStorage.setItem('erevis_kicked', data.replacedBy || 'another device')
        await signOut()
      }
    } catch {
      // Network error — don't kick, retry next interval
    }
  // Intentionally NOT depending on `session` or `sessionRef`: identity-stable
  // across token refresh; sessionRef is a ref object so its `.current` is
  // always fresh without re-binding the callback.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signOut])

  useEffect(() => {
    if (!session?.access_token) return

    // FA-01-19: stale-callback guard — payloads delivered after the
    // effect tears down (user change, unmount) must not still drive
    // checkSession().
    let effectActive = true

    // FA-01-19: Realtime is the IMMEDIACY trigger. Set it up
    // synchronously inside the effect body, BEFORE the 8s polling
    // startup delay, so a cross-device kick lands sub-5s rather than
    // waiting on the next 30s poll. Polling stays as the correctness
    // fallback — if Realtime never establishes or drops, polling still
    // catches invalidation on its normal cadence.
    //
    // Security boundary: this uses the browser Supabase client's
    // authenticated session (JWT). The `filter: user_id=eq.<id>` is
    // server-side narrowing for efficiency only; the actual gate is the
    // active_sessions RLS policy (auth.uid() = user_id, see
    // supabase/migrations/active_sessions.sql). No service-role client
    // exists or should exist on the frontend.
    const channel = supabase
      .channel(`active_sessions:${session.user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'active_sessions',
          filter: `user_id=eq.${session.user.id}`,
        },
        (payload) => {
          if (!effectActive || kickedRef.current) return
          const incomingToken = payload?.new?.session_token
          const ourToken = getSessionToken()
          if (!incomingToken || incomingToken === ourToken) return
          // Realtime is a TRIGGER only. The kick decision is made by
          // the server-authoritative /api/auth/session-check response.
          // Do NOT call signOut() here.
          checkSession()
        }
      )
      .subscribe((status, err) => {
        if (
          status === 'CHANNEL_ERROR' ||
          status === 'TIMED_OUT' ||
          status === 'CLOSED'
        ) {
          console.warn('[useSessionEnforcement] Realtime subscribe status:', status, err)
        }
      })

    // Don't poll immediately — wait for session registration to complete.
    startupTimerRef.current = setTimeout(() => {
      checkSession()
      intervalRef.current = setInterval(checkSession, CHECK_INTERVAL_MS)
    }, STARTUP_DELAY_MS)

    // Pause polling when tab is hidden, resume with delay when visible
    const handleVisibility = () => {
      if (document.hidden) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
      } else {
        if (visibilityTimerRef.current) clearTimeout(visibilityTimerRef.current)
        visibilityTimerRef.current = setTimeout(() => {
          if (!kickedRef.current) {
            checkSession()
            if (!intervalRef.current) {
              intervalRef.current = setInterval(checkSession, CHECK_INTERVAL_MS)
            }
          }
        }, VISIBILITY_DELAY_MS)
      }
    }

    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      effectActive = false
      if (startupTimerRef.current) clearTimeout(startupTimerRef.current)
      if (visibilityTimerRef.current) clearTimeout(visibilityTimerRef.current)
      if (intervalRef.current) clearInterval(intervalRef.current)
      document.removeEventListener('visibilitychange', handleVisibility)
      // removeChannel returns a promise resolving to 'ok' / 'error' /
      // 'timed out'. Don't await in cleanup — fire-and-forget with a
      // warn for non-ok statuses.
      void supabase.removeChannel(channel).then((status) => {
        if (status !== 'ok') {
          console.warn('[useSessionEnforcement] removeChannel:', status)
        }
      }).catch((err) => {
        console.warn('[useSessionEnforcement] removeChannel threw:', err)
      })
    }
  }, [session?.user?.id, checkSession]) // Re-run on user change (login/logout), NOT on token refresh
}
